import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  MousePointer,
  Package,
  Target,
  ClipboardList,
  TestTube,
  FlaskConical,
  MessageCircle,
  Settings,
  Car,
  Wand2,
} from 'lucide-react';
import SimulationCard from '../../components/SimulationCard';
import { isUnrestrictedUseCases } from '../../config/surfaceUseCases';
import { useAuth } from '../../context/AuthContext';
import { wiringValue } from '../../types/subscription';
import { useSubscription } from '../../context/subscription/SubscriptionContext';
import { LockedFeature } from '../../components/subscription/UpgradePrompt';

export type UseCaseType =
  | 'survey-and-focus-groups'
  | 'concept-test'
  | 'driving-concept-test'
  | 'vitamin-concept-test'
  | 'packaging-review-v2'
  | 'packaging-variant-comparison'
  | 'claims-testing'
  | 'attribution'
  | 'channel-strategy'
  | 'image-survey'
  | 'healthcare-chat-v2'
  | 'bluepill-chat'
  | 'hbs-chat'
  | 'test-use-case'
  | 'survey-creator'
  | 'survey-chat-154'
  | null;

interface UseCaseOption {
  label: string;
  type: UseCaseType;
  icon: React.ReactNode;
  description: string;
  redirect?: boolean;
}

interface CategoryConfig {
  title: string;
  useCases: UseCaseOption[];
}

interface LandingScreenProps {
  onSelectUseCase: (useCaseType: UseCaseType, useCaseTitle: string) => void;
}

interface GetCategoriesOptions {
  isAdmin: boolean;
  /** This account's allowed use cases, or null when nothing narrows them.
   *
   *  Served by /plans/current, which resolves it from the caller's organization — the client's own
   *  ticked list, not the plan's, so a client on an enterprise plan is narrowed to what they bought.
   *  Replaces thirteen per-client booleans and the *_ALLOWED_DOMAINS build-time variables. */
  useCases?: string[] | null;
}

const getCategories = ({ isAdmin, useCases }: GetCategoriesOptions): CategoryConfig[] => {
  // ONE list decides every tile, replacing thirteen per-client booleans. The two rules below are not
  // the same rule, and collapsing them would change what people see:
  //
  //   allows — on by default, hidden only when a list exists and leaves it out. The tiles every
  //            account has always had.
  //   optIn  — hidden unless the list names it. These tiles previously required a per-client flag,
  //            so an account with no configuration at all must still not see them.
  const allows = (t: string) => isAdmin || !useCases || useCases.includes(t);
  const optIn = (t: string) => isAdmin || (!!useCases && useCases.includes(t));
  // `optIn` WITHOUT the admin bypass, for a card that duplicates another's label. The hbs tile
  // reads "Focus Group", the same as healthcare-chat-v2, so bypassing for admins shows two
  // identical cards — which is what an admin saw the moment this replaced `isHbsUser`.
  const listed = (t: string) => !!useCases && useCases.includes(t);

  const showVitaminConceptTest = optIn('vitamin-concept-test');
  const showDrivingConceptTest = optIn('driving-concept-test');
  const showAiConceptTest = isAdmin;

  return ([
    {
      title: 'Research',
      useCases: [
        {
          label: 'Surveys (Qual and Quant)',
          type: 'survey-and-focus-groups',
          icon: <ClipboardList className="w-5 h-5" />,
          description: 'Run qualitative and quantitative interviews to get deep insights',
        },
        {
          label: 'Focus Group',
          type: 'healthcare-chat-v2',
          icon: <MessageCircle className="w-5 h-5" />,
          description: 'Chat with an AI consumer-twin focus group',
          redirect: true,
        },
        // HBS Chat - only visible to HBS users (opens in new tab)
        ...(listed('hbs-chat') ? [{
          label: 'Focus Group',
          type: 'hbs-chat' as UseCaseType,
          icon: <MessageCircle className="w-5 h-5" />,
          description: 'Chat with an AI consumer-twin focus group',
          redirect: true,
        }] : []),
        // Survey Chat - visible to KNF, ArcherJerky, Amy's, and admin users
        ...(optIn('survey-chat-154') ? [{
          label: 'Human Survey Insights',
          type: 'survey-chat-154' as UseCaseType,
          icon: <ClipboardList className="w-5 h-5" />,
          description: 'Chat with your human survey data and instantly surface key insights and themes.',
          redirect: true,
        }] : []),
        // Hidden for now — access via /chat-with-bluepill directly
        // {
        //   label: 'Chat with Bluepill',
        //   type: 'bluepill-chat',
        //   icon: <MessageCircle className="w-5 h-5" />,
        //   description: 'Chat with AI Consumer Twins powered by Bluepill',
        //   redirect: true,
        // },
        ...(isAdmin ? [{
          label: 'Survey Creation Agent',
          type: 'survey-creator' as UseCaseType,
          icon: <Wand2 className="w-5 h-5" />,
          description: 'Co-create a survey with an AI agent — draft, refine, and save in one flow',
          redirect: true,
        }] : []),
        ...(isAdmin ? [{
          label: 'Test Use Case',
          type: 'test-use-case' as UseCaseType,
          icon: <Settings className="w-5 h-5" />,
          description: 'Simulate a test to get insights',
        }] : []),
      ],
    },
    {
      title: 'Product & Innovation',
      useCases: [
        // Vitamin Concept Test - shown as "Concept Test" for admins, demo, smartypants
        ...(showVitaminConceptTest ? [{
          label: 'Concept Test',
          type: 'vitamin-concept-test' as UseCaseType,
          icon: <TestTube className="w-5 h-5" />,
          description: 'Test concepts with AI-simulated populations',
        }] : []),
        // Driving Concept Test - shown as "Concept Test" for Panasonic users only
        ...(showDrivingConceptTest ? [{
          label: 'Concept Test',
          type: 'driving-concept-test' as UseCaseType,
          icon: <Car className="w-5 h-5" />,
          description: 'Test concepts with AI-simulated populations',
        }] : []),
        // AI Concept Test - visible to admins (but not knf user)
        ...(showAiConceptTest ? [{
          label: 'AI Concept Test',
          type: 'concept-test' as UseCaseType,
          icon: <TestTube className="w-5 h-5" />,
          description: 'Test product concepts with AI-simulated consumers to predict purchase intent',
        }] : []),
        {
          label: 'Packaging Review',
          type: 'packaging-review-v2',
          icon: <Package className="w-5 h-5" />,
          description: 'Analyze a packaging design with shelf test, competitive comparison, and consumer reactions',
        },
        {
          label: 'Packaging Variant Comparison',
          type: 'packaging-variant-comparison',
          icon: <Package className="w-5 h-5" />,
          description: 'Compare 2-5 packaging variants to find which drives highest purchase intent',
        },
        {
          label: 'Claims Testing',
          type: 'claims-testing',
          icon: <FlaskConical className="w-5 h-5" />,
          description: 'Test and rank product claims using MaxDiff analysis to find what resonates most',
        },
      ],
    },
    {
      title: 'Marketing',
      useCases: [
        {
          label: 'AB Test Messaging',
          type: 'attribution',
          icon: <Target className="w-5 h-5" />,
          description: 'Learn what drives or blocks conversions',
        },
        {
          label: 'A/B Test or Review Creatives and Ads',
          type: 'channel-strategy',
          icon: <TrendingUp className="w-5 h-5" />,
          description: 'Instantly predict which creative will perform best, get insights on what resonates',
        },
        {
          label: 'Creative Rank Test',
          type: 'image-survey',
          icon: <MousePointer className="w-5 h-5" />,
          description: 'Run a ranked click test to identify the best creatives for your listing',
        },
      ],
    },
  ] as CategoryConfig[]).map((category) => ({
    // The default-on tiles above are written unconditionally and narrowed here, so a restricted
    // account cannot be handed one by a tile someone forgot to gate. The opt-in tiles have already
    // been filtered by `optIn`, and pass this unchanged because a list that names them contains them.
    ...category,
    useCases: category.useCases.filter((useCase) => allows(useCase.type as string)),
  })).filter((category) => category.useCases.length > 0);
};

const LandingScreen: React.FC<LandingScreenProps> = ({ onSelectUseCase }) => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { hasFeatureAccess, currentSubscription, allowedUseCases, entitlementsLoading,
          clientConfig, surveyChatTargets } = useSubscription();
  // The plan's use-case allow-list. NARROWS ONLY, and only when the backend sent a concrete list:
  // `'all'` (the sentinel) and `[]` (a plan nobody configured) both mean unrestricted, because reading
  // silence as "nothing" would empty the dashboard for every plan that exists today — all of them are
  // silent here. Same rule the sidebar's surface filter uses.
  // Via the shared predicate, so the tiles and the route guard cannot drift apart. Same behaviour as the inline
  // ternary this replaces; `LandingUseCaseAllowList.test.tsx` holds it to that.
  const planUseCases = isUnrestrictedUseCases(allowedUseCases)
    ? null
    : (allowedUseCases as string[]);

  // Both lists narrow, so both apply — the INTERSECTION, which is what the old code did by running
  // the client filter and the plan filter in series, and what the db resolver's
  // `merge_plan_use_cases_with_org` computes server-side. Taking only the client's would hand a
  // restricted plan a card it does not sell.
  const clientUseCases = clientConfig?.useCases ?? null;
  const useCases = clientUseCases && planUseCases
    ? clientUseCases.filter((u) => planUseCases.includes(u))
    : (clientUseCases ?? planUseCases);

  const categories = getCategories({ isAdmin, useCases });

  const handleUseCaseClick = (useCase: UseCaseOption) => {
    if (useCase.redirect) {
      if (useCase.type === 'healthcare-chat-v2') {
        navigate('/ask-natural-organic');
        return;
      }
      if (useCase.type === 'bluepill-chat') {
        navigate('/chat-with-bluepill');
        return;
      }
      if (useCase.type === 'hbs-chat') {
        window.open('/ask-natural-organic', '_blank');
        return;
      }
      if (useCase.type === 'survey-creator') {
        navigate('/survey-creator');
        return;
      }
      if (useCase.type === 'survey-chat-154') {
        // Landing entry point = the client's own standalone survey; the in-chat switcher then lets
        // them change surveys. The server decides which that is — the tile is only offered to an
        // account whose list names it, so the first target is theirs.
        // WHICH survey this card opens is a decision, so it is configurable: client_wiring
        // landing/survey_chat_entry names it. Unset, it falls back to the first target that
        // carries a SURVEY — a client can also hold a survey GROUP, which has no surveyId, and
        // targets arrive in grant order, so Archer Jerky's and Kettle & Fire's groups came first.
        // Taking targets[0] left the card rendering and then doing nothing when clicked.
        const entrySurveyId =
          wiringValue<number | null>(clientConfig, 'landing', 'survey_chat_entry', null)
          ?? surveyChatTargets?.find((t) => t.surveyId)?.surveyId;
        if (!entrySurveyId) return;
        navigate(`/surveys/${entrySurveyId}/chat?from=landing`);
        return;
      }
    }

    onSelectUseCase(useCase.type, useCase.label);
  };

  // Check if a use case should be disabled for Panasonic users

  return (
    <div className="max-w-6xl mx-auto animate-fadeIn">
      <div className="text-center mb-10">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Start a new study.
        </h1>
        <p className="text-gray-500">
          Run research, test ideas, and evaluate marketing with your AI audience.
        </p>
      </div>

      {entitlementsLoading ? (
        /* Entitlements not answered yet. Drawing the cards now means drawing the DEFAULTS — the
           unrestricted `'all'` sentinel plus `featureAccess === null`, i.e. every card, most of them locked
           behind a "Subscribe to get access" upsell — and then replacing them a moment later. That flicker
           is also a live window in which a restricted account can click a use case its plan forbids, and an
           upsell shown to a cohort that has no self-serve billing. A placeholder says "not yet", which is
           the truth; absence of data must never be drawn as a definite answer. */
        <div data-testid="use-case-cards-loading" className="grid grid-cols-1 md:grid-cols-3 gap-8" aria-busy="true">
          {[0, 1, 2].map((column) => (
            <div key={column}>
              <div className="h-6 w-32 bg-gray-100 rounded animate-pulse mb-4" />
              <div className="flex flex-col gap-4">
                {[0, 1, 2].map((card) => (
                  <div key={card} className="h-24 bg-gray-100 rounded-xl animate-pulse" />
                ))}
              </div>
            </div>
          ))}
          <span className="sr-only">Loading your studies…</span>
        </div>
      ) : (() => {
        // Calculate visible categories for dynamic grid sizing
        // getCategories has already applied the list and dropped any category left empty, so a
        // heading with nothing under it — and the grid sized for a column that never fills — cannot
        // happen here. The filtering used to be repeated at this level against two separate lists.
        const visibleCategories = categories;

        // Dynamic grid classes based on visible category count
        const gridClasses = visibleCategories.length === 2
          ? "grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto"
          : visibleCategories.length === 1
            ? "grid grid-cols-1 gap-8 max-w-md mx-auto"
            : "grid grid-cols-1 md:grid-cols-3 gap-8";

        return (
          <div className={gridClasses}>
            {visibleCategories.map((category) => (
          <div key={category.title}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {category.title}
            </h3>
            <div className="flex flex-col gap-4">
              {category.useCases.map((useCase) => {
                const isChatRedirect = useCase.redirect;
                // A card the account's own list names is UNLOCKED without a subscription check —
                // it is what they bought. This one rule replaces the per-client access bypasses
                // (Panasonic's driving test, HBS's chat, each registered client's own tiles), which
                // were the same statement written once per client.
                const isClientAccess = !!clientUseCases && useCase.type != null && clientUseCases.includes(useCase.type);
                const hasAccess = isAdmin || isChatRedirect || isClientAccess || hasFeatureAccess(useCase.type as string);
                const isLocked = !hasAccess;

                if (isLocked) {
                  return (
                    <LockedFeature
                      key={useCase.type}
                      feature={useCase.label}
                      requiredPlan={currentSubscription?.id === 'free' ? 'Starter' : 'Higher tier'}
                    >
                      <SimulationCard
                        icon={useCase.icon}
                        title={useCase.label}
                        description={useCase.description}
                        disabled
                        onClick={() => {}}
                      />
                    </LockedFeature>
                  );
                }

                return (
                  <SimulationCard
                    key={useCase.type}
                    icon={useCase.icon}
                    title={useCase.label}
                    description={useCase.description}
                    onClick={() => handleUseCaseClick(useCase)}
                  />
                );
              })}
            </div>
          </div>
        ))}
          </div>
        );
      })()}
    </div>
  );
};

export default LandingScreen;
